import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Activity, TrendingUp, Database, Filter, Percent, Fuel } from "lucide-react";

const MEFuelMCR = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      category: "Source Data Fields",
      items: [
        {
          name: "mcr",
          source: "VesselMainEngineData",
          description: "Maximum Continuous Rating percentage",
          calculation: "Direct value from CSV import (pipe-separated)"
        },
        {
          name: "fuelConsumption",
          source: "VesselMainEngineData",
          description: "Total fuel consumption in metric tons",
          calculation: "Sum of HFO + LFO + MGO consumption"
        },
        {
          name: "engineId",
          source: "VesselMainEngineData",
          description: "Engine identifier",
          calculation: "Pipe-separated values from CSV"
        },
        {
          name: "voyageId",
          source: "VesselMainEngineData",
          description: "Voyage identifier",
          calculation: "Linked from voyage data"
        }
      ]
    },
    {
      category: "Calculated Fields",
      items: [
        {
          name: "mcr",
          source: "Daily average",
          description: "Average MCR percentage per day",
          calculation: "Average of all MCR values for each day"
        },
        {
          name: "consumption",
          source: "Daily average",
          description: "Average fuel consumption per day",
          calculation: "Average of all fuel consumption values for each day"
        },
        {
          name: "median",
          source: "Calculated estimate",
          description: "Estimated median consumption",
          calculation: "Average consumption × 0.85"
        }
      ]
    }
  ];

  const calculations = [
    {
      title: "Daily Average MCR",
      formula: "∑ mcr ÷ count(records)",
      description: "Average Maximum Continuous Rating percentage for each day",
      parameters: ["MCR range: 0-100%", "Exclude null/invalid values", "Daily grouping"]
    },
    {
      title: "Daily Average Consumption",
      formula: "∑ fuelConsumption ÷ count(records)",
      description: "Average fuel consumption in metric tons for each day",
      parameters: ["Consumption ≥ 0", "Exclude null values", "Metric tons precision"]
    },
    {
      title: "Median Consumption Estimate",
      formula: "Average consumption × 0.85",
      description: "Industry standard estimate for typical consumption",
      parameters: ["85% factor", "Used for comparison", "Performance benchmark"]
    },
  ];

  const responseStructure = [
    {
      field: "date",
      type: "string",
      format: "YYYY-MM-DD",
      description: "Calendar date",
      example: "2024-01-15"
    },
    {
      field: "mcr",
      type: "number",
      format: "percentage",
      description: "Average MCR percentage",
      example: "85.5"
    },
    {
      field: "median",
      type: "number",
      format: "metric tons",
      description: "Estimated median consumption",
      example: "10.2"
    },
    {
      field: "consumption",
      type: "number",
      format: "metric tons",
      description: "Average fuel consumption",
      example: "12.0"
    }
  ];

  const validationRules = [
    {
      rule: "MCR Range",
      condition: "0 ≤ mcr ≤ 100",
      purpose: "Ensure valid percentage values"
    },
    {
      rule: "Fuel Consumption",
      condition: "consumption ≥ 0",
      purpose: "Prevent negative consumption values"
    },
    {
      rule: "Data Completeness",
      condition: "mcr ≠ null AND consumption ≠ null",
      purpose: "Only include valid records"
    },
    {
      rule: "Engine Matching",
      condition: "engineId matches pattern",
      purpose: "Filter specific engine data"
    }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#d5e7f7] rounded-lg">
              <TrendingUp className="w-6 h-6 text-[#566573]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Fuel vs Maximum Continuous Rating (MCR)
              </h2>
              <p className="text-gray-600 text-sm">
                Correlation analysis between engine load and fuel consumption
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            
            {/* Data Sources */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Data Sources & Fields
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {dataSources.map((source, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-gray-700 mb-3">{source.category}</h4>
                    <div className="space-y-3">
                      {source.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-medium text-gray-800">{item.name}</span>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {item.source}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">{item.description}</p>
                          <p className="text-xs text-gray-500">Calculation: {item.calculation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Calculations */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Core Calculations
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {calculations.map((calc, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Percent className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800">{calc.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{calc.description}</p>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                      <p className="text-sm font-mono bg-gray-50 p-2 rounded border">
                        {calc.formula}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Parameters:</p>
                      <div className="flex flex-wrap gap-1">
                        {calc.parameters.map((param, paramIdx) => (
                          <span key={paramIdx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {param}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MEFuelMCR;